import React, { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/authApi';
import {
  CredentialsDTO,
  RegisterDTO,
  RegisterUserDTO,
  ResetPasswordRequestDTO,
  ResetPasswordConfirmDTO,
} from '../dtos';
import { UserRole } from '../types';
import { useAuthStore, authService } from '../store/authStore';
import { usePostHog } from '../../../hooks/usePostHog';
import { AUTH_EVENTS } from '../../../utils/trackingEvents';

// Query keys
export const authQueryKeys = {
  currentUser: ['auth', 'currentUser'] as const,
  userSession: ['auth', 'userSession'] as const,
  accessibleTenants: ['auth', 'accessibleTenants'] as const,
};

// Hook to get current authenticated user from backend (legacy)
export const useCurrentUser = () => {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: authQueryKeys.currentUser,
    queryFn: authApi.getCurrentUser,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};

// Hook to get current user session with multi-tenant context
export const useUserSession = () => {
  const { isAuthenticated, clearAuth } = useAuthStore();

  const query = useQuery({
    queryKey: authQueryKeys.userSession,
    queryFn: authApi.getCurrentSession,
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequent than basic user)
    retry: false,
  });

  // Handle errors using React Query v5 pattern with useEffect
  React.useEffect(() => {
    if (query.error) {
      // Check if this is an authentication/authorization error
      const error = query.error as any;
      const isAuthError =
        error?.status === 401 ||
        error?.status === 403 ||
        error?.status === 422 ||
        error?.message?.includes('unauthorized') ||
        error?.message?.includes('forbidden') ||
        error?.message?.includes('invalid');

      if (isAuthError) {
        (async () => {
          try {
            // Import auth service dynamically to avoid circular dependency
            const { authService } = await import('../store/authStore');
            await authService.logout();

            // Clear auth store state
            clearAuth();

            // Redirect to login if not already there
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          } catch (logoutError) {
            console.error('Auto-logout failed:', logoutError);
            // Still clear auth store even if logout fails
            clearAuth();

            // Force redirect to login
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        })();
      }
    }
  }, [query.error, clearAuth]);

  return query;
};

// Hook to get accessible tenants
export const useAccessibleTenants = () => {
  const { isAuthenticated, setAccessibleTenants, setCurrentTenant } = useAuthStore();

  return useQuery({
    queryKey: authQueryKeys.accessibleTenants,
    queryFn: async () => {
      const result = await authApi.getAccessibleTenants();

      // Sync with auth store
      if (result.tenants) {
        setAccessibleTenants(result.tenants);

        // Update current tenant if it's not set or invalid
        const { currentTenant } = useAuthStore.getState();
        if (!currentTenant || !result.tenants.find((t) => t.id === currentTenant.id)) {
          const activeTenant = result.tenants.find((t) => t.id === result.currentTenantId);
          if (activeTenant) {
            setCurrentTenant(activeTenant);
          }
        }
      }

      return result;
    },
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
  });
};

// Hook for Cognito login
export const useLogin = () => {
  const queryClient = useQueryClient();
  const { setAuth, clearAuth } = useAuthStore();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: async (credentials: CredentialsDTO) => {
      try {
        const response = await authService.authenticate(credentials);

        // Don't throw an error for first login scenarios or MFA scenarios
        if (
          !response.isSignedIn &&
          !response.isFirstLogin &&
          !response.requiresMFA &&
          response.errorMessage
        ) {
          throw new Error(response.errorMessage);
        }
        return response;
      } catch (error: unknown) {
        // If the error contains "additional steps", it might be a first login scenario
        if (error instanceof Error && error.message && error.message.includes('additional steps')) {
          return {
            isSignedIn: false,
            isFirstLogin: true,
          };
        }

        throw error;
      }
    },
    onSuccess: async (data) => {
      // Only handle automatic sign-in for fully authenticated users
      if (data.isSignedIn) {
        try {
          // Get user info after successful authentication
          const userInfo = await authService.loadUser();

          // Track successful login
          captureEvent(AUTH_EVENTS.LOGIN, {
            login_method: 'email',
            is_first_login: false,
          });

          // Convert to UserDTO for store compatibility
          const user = {
            id: userInfo.id,
            email: userInfo.email,
            name: userInfo.name,
            cognitoId: userInfo.id, // Cognito user ID from the 'sub' attribute
          };

          // Get current tokens from the auth service
          const currentTokens = authService.getCurrentTokens();
          const tokens = currentTokens
            ? {
                accessToken: currentTokens.accessToken || '',
                idToken: currentTokens.idToken || '',
                refreshToken: currentTokens.refreshToken || '',
              }
            : undefined;

          // Try to load tenant context
          let userSession;
          try {
            userSession = await authApi.getCurrentSession();
          } catch (tenantError) {
            console.error('Failed to load tenant context during login:', tenantError);
          }

          // Store auth data with tenant context AND tokens
          setAuth(user, tokens, userSession);

          // Cache user data
          queryClient.setQueryData(authQueryKeys.currentUser, user);
          if (userSession) {
            queryClient.setQueryData(authQueryKeys.userSession, userSession);
            queryClient.setQueryData(authQueryKeys.accessibleTenants, {
              tenants: userSession.accessibleTenants,
              totalCount: userSession.accessibleTenants.length,
              currentTenantId: userSession.currentTenantId,
            });
          }

          // Invalidate and refetch data from backend
          queryClient.invalidateQueries({ queryKey: authQueryKeys.currentUser });
          queryClient.invalidateQueries({ queryKey: authQueryKeys.userSession });
          queryClient.invalidateQueries({ queryKey: authQueryKeys.accessibleTenants });
        } catch (error) {
          console.error('Failed to load user after login:', error);
          clearAuth();
        }
      }
      // For first login (NEW_PASSWORD_REQUIRED), we don't process here - let the component handle it
    },
    onError: (error) => {
      // Don't clear auth for first login scenarios, let the component handle the flow
      console.error('Login mutation error:', error);
      clearAuth();
    },
  });
};

// Hook for Cognito registration
export const useRegister = () => {
  return useMutation({
    mutationFn: async (_userData: RegisterDTO) => {
      // The new auth service doesn't support direct registration
      // Registration is handled differently (admin creates users, users set password on first login)
      throw new Error(
        'Registration is not supported with the new authentication service. Users must be created by an administrator.'
      );
    },
    onError: (error) => {
      console.error('Registration error:', error);
    },
  });
};

// Hook for setting new password (first login)
export const useSetNewPassword = () => {
  const queryClient = useQueryClient();
  const { setAuth, clearAuth } = useAuthStore();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: async (userData: RegisterUserDTO) => {
      const result = await authService.initUser(userData);
      if (!result) {
        throw new Error('Failed to set new password');
      }
      return result;
    },
    onSuccess: async () => {
      try {
        // Get user info after successful password setup
        const userInfo = await authService.loadUser();

        // Track first login completion
        captureEvent(AUTH_EVENTS.LOGIN, {
          login_method: 'email',
          is_first_login: true,
          password_changed: true,
        });

        // Convert to UserDTO for store compatibility
        const user = {
          id: userInfo.id,
          email: userInfo.email,
          name: userInfo.name,
          cognitoId: userInfo.id, // Cognito user ID from the 'sub' attribute
        };

        // Get current tokens from the auth service
        const currentTokens = authService.getCurrentTokens();
        const tokens = currentTokens
          ? {
              accessToken: currentTokens.accessToken || '',
              idToken: currentTokens.idToken || '',
              refreshToken: currentTokens.refreshToken || '',
            }
          : undefined;

        // Try to load tenant context
        let userSession;
        try {
          userSession = await authApi.getCurrentSession();
        } catch (tenantError) {
          console.error('Failed to load tenant context after password setup:', tenantError);
        }

        // Store auth data with tenant context AND tokens
        setAuth(user, tokens, userSession);

        // Cache user data
        queryClient.setQueryData(authQueryKeys.currentUser, user);
        if (userSession) {
          queryClient.setQueryData(authQueryKeys.userSession, userSession);
          queryClient.setQueryData(authQueryKeys.accessibleTenants, {
            tenants: userSession.accessibleTenants,
            totalCount: userSession.accessibleTenants.length,
            currentTenantId: userSession.currentTenantId,
          });
        }

        // Invalidate and refetch data from backend
        queryClient.invalidateQueries({ queryKey: authQueryKeys.currentUser });
        queryClient.invalidateQueries({ queryKey: authQueryKeys.userSession });
        queryClient.invalidateQueries({ queryKey: authQueryKeys.accessibleTenants });
      } catch (error) {
        console.error('Failed to load user after password setup:', error);
        clearAuth();
        throw error;
      }
    },
    onError: () => {
      clearAuth();
    },
  });
};

// Hook for Cognito logout
export const useLogout = () => {
  const queryClient = useQueryClient();
  const { clearAuth } = useAuthStore();
  const { captureEvent, reset: resetPostHog } = usePostHog();

  const performComprehensiveLogout = useCallback(async () => {
    try {
      // 1. Clear authentication state
      clearAuth();

      // 2. Clear React Query cache
      queryClient.clear();

      // 3. Clear role context storage to prevent role contamination on next login
      try {
        const { roleContextStorage } = await import('../../role-context/utils/roleContextStorage');
        roleContextStorage.clearCurrentRoleContext();
      } catch (roleContextError) {
        console.error('Failed to clear role context storage:', roleContextError);
      }

      // 4. Clear context store (localStorage context-storage)
      try {
        const { useContextStore } = await import('../../../stores/contextStore');
        const contextStore = useContextStore.getState();
        contextStore.clearActiveContext();
      } catch (contextStoreError) {
        console.error('Failed to clear context store:', contextStoreError);
      }

      // 5. Reset PostHog to clear user identity and groups
      try {
        resetPostHog();
      } catch (posthogError) {
        console.error('PostHog reset failed:', posthogError);
      }
    } catch (error) {
      console.error('Error during comprehensive logout:', error);
      // Still clear auth even if other cleanup fails
      clearAuth();
      queryClient.clear();
    }
  }, [clearAuth, queryClient, resetPostHog]);

  return useMutation({
    mutationFn: async () => {
      // Track logout event before clearing session
      captureEvent(AUTH_EVENTS.LOGOUT, {
        logout_type: 'manual',
      });

      await authService.logout();
    },
    onSuccess: () => {
      performComprehensiveLogout();
    },
    onError: (error) => {
      console.error('Logout error:', error);
      // Clear everything anyway on error to prevent stuck states
      performComprehensiveLogout();
    },
  });
};

// Convenience hooks
export const useIsAuthenticated = () => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated;
};

export const useUser = () => {
  const { user } = useAuthStore();
  return user;
};

// Hook for password reset request
export const usePasswordReset = () => {
  return useMutation({
    mutationFn: async (data: ResetPasswordRequestDTO) => {
      await authService.passwordReset(data);
    },
    onError: (error) => {
      console.error('Password reset request error:', error);
    },
  });
};

// Hook for password reset confirmation
export const usePasswordResetConfirm = () => {
  return useMutation({
    mutationFn: async (data: ResetPasswordConfirmDTO) => {
      await authService.passwordResetConfirm(data);
    },
    onError: (error) => {
      console.error('Password reset confirmation error:', error);
    },
  });
};

// Multi-tenant mutation hooks

// Hook for tenant switching
export const useSwitchTenant = () => {
  const queryClient = useQueryClient();
  const { switchTenant, currentTenant } = useAuthStore();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: async (tenantId: string) => {
      const previousTenantId = currentTenant?.id;
      await switchTenant(tenantId);
      return { tenantId, previousTenantId };
    },
    onSuccess: ({ tenantId, previousTenantId }) => {
      // Track tenant switch
      captureEvent(AUTH_EVENTS.TENANT_SWITCH, {
        from_tenant_id: previousTenantId,
        to_tenant_id: tenantId,
      });

      // Invalidate all auth-related queries after tenant switch
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error) => {
      console.error('Tenant switch error:', error);
    },
  });
};

// Hook for refreshing tenant access
export const useRefreshTenantAccess = () => {
  const queryClient = useQueryClient();
  const { refreshTenantAccess } = useAuthStore();

  return useMutation({
    mutationFn: refreshTenantAccess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authQueryKeys.accessibleTenants });
    },
    onError: (error) => {
      console.error('Refresh tenant access error:', error);
    },
  });
};

// Hook for refreshing user context
export const useRefreshUserContext = () => {
  const queryClient = useQueryClient();
  const { refreshUserContext } = useAuthStore();

  return useMutation({
    mutationFn: refreshUserContext,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authQueryKeys.userSession });
    },
    onError: (error) => {
      console.error('Refresh user context error:', error);
    },
  });
};

// Multi-tenant convenience hooks
export const useCurrentTenant = () => {
  const { currentTenant } = useAuthStore();
  return currentTenant;
};

export const useAccessibleTenantsStore = () => {
  const { accessibleTenants } = useAuthStore();
  return accessibleTenants;
};

export const useUserSessionStore = () => {
  const { userSession } = useAuthStore();
  return userSession;
};

export const useTenantSwitchState = () => {
  const { isSwitchingTenant, tenantSwitchError } = useAuthStore();
  return { isSwitchingTenant, tenantSwitchError };
};

// Role and permission hooks
export const useHasRole = (role: UserRole | string) => {
  const { hasRole } = useAuthStore();
  return hasRole(role.toString());
};

export const useCurrentRoles = () => {
  const { getCurrentRoles } = useAuthStore();
  return getCurrentRoles();
};

export const useCanSwitchTenants = () => {
  const { canSwitchTenants } = useAuthStore();
  return canSwitchTenants();
};

// Main useAuth hook - enhanced with multi-tenant support
export const useAuth = () => {
  const store = useAuthStore();

  return {
    // Existing auth state
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,

    // Multi-tenant state
    currentTenant: store.currentTenant,
    accessibleTenants: store.accessibleTenants,
    userSession: store.userSession,
    isSwitchingTenant: store.isSwitchingTenant,
    tenantSwitchError: store.tenantSwitchError,

    // Actions
    login: useLogin().mutateAsync,
    logout: useLogout().mutateAsync,
    switchTenant: store.switchTenant,
    refreshTenantAccess: store.refreshTenantAccess,
    refreshUserContext: store.refreshUserContext,

    // Convenience methods
    hasRole: store.hasRole,
    getCurrentRoles: store.getCurrentRoles,
    canSwitchTenants: store.canSwitchTenants(),

    // Computed properties
    currentRoles: store.userSession?.currentRoles || [],
    permissions: store.userSession?.permissions || [],
  };
};
